import React, { useState, useEffect } from 'react';
import StepEmail from './StepEmail';
import StepPlans from './StepPlans';
import StepProject from './StepProject';
import StepAssets from './StepAssets';
import StepCompanyInfo from './StepCompanyInfo';
import StepReferralSource from './StepReferralSource';
import StepRfqPreference from './StepRfqPreference';
import StepThinking from './StepThinking';
import styles from './styles/BrandSignup.module.css';

// LocalStorage key for storing current step
const STORAGE_KEY_PREFIX = 'brandSignup_currentStep_';

const BrandSignupApp = ({
  brandSignup: initialBrandSignup,
  stepFlow,
  categories,
  csrfToken,
  featureFlags,
  signupPath,
  completeStepPath,
  skipStepPath,
  skipToDashboardPath
}) => {
  const [brandSignup, setBrandSignup] = useState(initialBrandSignup);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Separate "display step" for UI navigation vs backend's current_step
  const [displayStep, setDisplayStep] = useState(initialBrandSignup.current_step);

  // Get step order from backend stepFlow prop
  const STEP_ORDER = Object.keys(stepFlow).sort((a, b) => {
    return stepFlow[a].order - stepFlow[b].order;
  });

  // Always use backend's current_step on mount/refresh to prevent going to end of onboarding
  useEffect(() => {
    setDisplayStep(initialBrandSignup.current_step);
  }, [initialBrandSignup.id]);

  // Save display step to localStorage whenever it changes
  useEffect(() => {
    if (displayStep) {
      const storageKey = `${STORAGE_KEY_PREFIX}${initialBrandSignup.id}`;
      localStorage.setItem(storageKey, displayStep);
    }
  }, [displayStep, initialBrandSignup.id]);

  // Handle back navigation (UI only - doesn't affect backend)
  const goToPreviousStep = () => {
    const currentIndex = STEP_ORDER.indexOf(displayStep);
    if (currentIndex > 0) {
      const previousStep = STEP_ORDER[currentIndex - 1];
      setDisplayStep(previousStep);
    }
  };

  // Check if we can go back
  const canGoBack = () => {
    const currentIndex = STEP_ORDER.indexOf(displayStep);
    return currentIndex > 0;
  };

  const makeRequest = async (url, method = 'POST', data = {}, isFormData = false) => {
    const headers = {
      'X-CSRF-Token': csrfToken,
      'Accept': 'application/json'
    };

    let body;
    if (isFormData) {
      body = data; // FormData automatically sets Content-Type
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(data);
    }

    const response = await fetch(url, {
      method,
      headers,
      body
    });

    if (!response.ok) {
      // Check if response is JSON before trying to parse it
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.errors?.[0] || 'An error occurred');
      } else {
        // If it's not JSON (likely HTML error page), throw a generic error
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
    }

    return response.json();
  };

  const completeStep = async (stepName, stepData, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      let requestData;
      const isFormData = options.isFileUpload && stepData instanceof FormData;

      if (isFormData) {
        // Add step name to FormData
        stepData.append('step_name', stepName);
        requestData = stepData;
      } else {
        requestData = {
          step_name: stepName,
          step_data: stepData
        };
      }

      const result = await makeRequest(completeStepPath, 'POST', requestData, isFormData);

      if (result.completed) {
        // Redirect to completion page
        window.location.href = result.redirect_url;
      } else if (result.redirect_url) {
        // Redirect to external URL (e.g., Stripe checkout)
        window.location.href = result.redirect_url;
      } else {
        // Update backend state
        setBrandSignup(result.brand_signup);

        // For display step, advance sequentially (not jump to backend's next step)
        const currentIndex = STEP_ORDER.indexOf(stepName);
        if (currentIndex >= 0 && currentIndex < STEP_ORDER.length - 1) {
          const nextDisplayStep = STEP_ORDER[currentIndex + 1];
          setDisplayStep(nextDisplayStep);

          // Add plan_selected parameter to URL after plans step
          if (stepName === 'plans' && stepData?.selected_tier) {
            const url = new URL(window.location.href);
            url.searchParams.set('plan_selected', stepData.selected_tier);
            window.history.replaceState({}, '', url.toString());
          }
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const skipStep = async (stepName) => {
    setLoading(true);
    setError(null);

    try {
      const result = await makeRequest(skipStepPath, 'POST', {
        step_name: stepName
      });

      // Update backend state
      setBrandSignup(result.brand_signup);

      // For display step, advance sequentially
      const currentIndex = STEP_ORDER.indexOf(stepName);
      if (currentIndex >= 0 && currentIndex < STEP_ORDER.length - 1) {
        const nextDisplayStep = STEP_ORDER[currentIndex + 1];
        setDisplayStep(nextDisplayStep);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateBrandSignup = async (data) => {
    setLoading(true);
    setError(null);

    try {
      const result = await makeRequest(signupPath, 'PATCH', {
        brand_signup: data
      });

      setBrandSignup(result.brand_signup);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const skipToDashboard = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await makeRequest(skipToDashboardPath, 'POST', {});

      if (result.redirect_url) {
        window.location.href = result.redirect_url;
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const renderCurrentStep = () => {
    // Use displayStep for rendering (allows back button navigation)
    const stepName = displayStep;

    switch (stepName) {
      case 'email':
        return (
          <StepEmail
            brandSignup={brandSignup}
            onComplete={(data) => completeStep('email', data)}
            onUpdate={updateBrandSignup}
            onBack={goToPreviousStep}
            canGoBack={canGoBack()}
            loading={loading}
            error={error}
            showLuna={featureFlags?.showLunaOnEmailStep}
          />
        );

      case 'plans':
        return (
          <StepPlans
            brandSignup={brandSignup}
            onComplete={(data) => completeStep('plans', data)}
            onBack={goToPreviousStep}
            canGoBack={canGoBack()}
            loading={loading}
            error={error}
          />
        );

      case 'project_details':
        return (
          <StepProject
            brandSignup={brandSignup}
            categories={categories}
            onComplete={(data) => completeStep('project_details', data)}
            onUpdate={updateBrandSignup}
            onSkipToDashboard={skipToDashboard}
            onBack={goToPreviousStep}
            canGoBack={canGoBack()}
            loading={loading}
            error={error}
          />
        );

      case 'assets':
        return (
          <StepAssets
            brandSignup={brandSignup}
            onComplete={(data, options) => completeStep('assets', data, options)}
            onUpdate={updateBrandSignup}
            onSkipToDashboard={skipToDashboard}
            onBack={goToPreviousStep}
            canGoBack={canGoBack()}
            loading={loading}
            error={error}
          />
        );

      case 'company_info':
        return (
          <StepCompanyInfo
            brandSignup={brandSignup}
            onComplete={(data) => completeStep('company_info', data)}
            onUpdate={updateBrandSignup}
            onSkipToDashboard={skipToDashboard}
            onBack={goToPreviousStep}
            canGoBack={canGoBack()}
            loading={loading}
            error={error}
          />
        );

      case 'referral_source':
        return (
          <StepReferralSource
            brandSignup={brandSignup}
            onComplete={(data) => completeStep('referral_source', data)}
            onSkipToDashboard={skipToDashboard}
            onBack={goToPreviousStep}
            canGoBack={canGoBack()}
            loading={loading}
            error={error}
          />
        );

      case 'rfq_preference':
        return (
          <StepRfqPreference
            brandSignup={brandSignup}
            onComplete={(data) => completeStep('rfq_preference', data)}
            onSkipToDashboard={skipToDashboard}
            onBack={goToPreviousStep}
            canGoBack={canGoBack()}
            loading={loading}
            error={error}
          />
        );

      case 'thinking':
        return (
          <StepThinking
            brandSignup={brandSignup}
            onComplete={(data) => completeStep('thinking', data)}
            onSkipToDashboard={skipToDashboard}
            onBack={goToPreviousStep}
            canGoBack={canGoBack()}
            loading={loading}
            error={error}
          />
        );

      default:
        return (
          <div className="notification is-warning">
            <p>Unknown step: {stepName}. Please refresh the page.</p>
          </div>
        );
    }
  };

  return (
    <div className={styles.brandSignupContainer}>
      {renderCurrentStep()}

      {loading && (
        <div className="modal is-active">
          <div className="modal-background"></div>
          <div className="modal-content">
            <div className="box has-text-centered">
              <div className="loader is-loading mb-3"></div>
              <p>Processing...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandSignupApp;